import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@service/auth.service';
import { SettingsService } from '@service/settings.service';

@Injectable()
export class AuthGuard {
  private router = inject(Router);
  private authService = inject(AuthService);
  private settingsService = inject(SettingsService);

  /**
   * Determines whether the route can be activated based on application settings and authentication status.
   *
   * - If the base URL is invalid, navigates to the settings page and prevents activation.
   * - If the authentication token is invalid, navigates to the login page and prevents activation.
   * - Returns `true` if both the base URL and authentication token are valid, allowing route activation.
   *
   * @returns {boolean} `true` if the route can be activated; otherwise, `false`.
   */
  canActivate() {
    if (!this.settingsService.hasValidBaseUrl()) {
      this.router.navigate(['taskpane', 'settings'], { queryParams: { requireBaseUrl: true } });
      return false;
    } else if (!this.authService.hasValidToken()) {
      this.router.navigate(['taskpane', 'login']);
      return false;
    }
    return true;
  }
}

/**
 * Guard that checks for valid base URL and authentication token without redirecting.
 *
 * This guard uses `SettingsService` to verify the application's base URL is valid,
 * and `AuthService` to ensure the user has a valid authentication token.
 * If both conditions are met, route activation is allowed.
 *
 * @example
 * {
 *   path: 'protected',
 *   canActivate: [AuthGuardNoRedirect],
 *   component: ProtectedComponent
 * }
 */
@Injectable()
export class AuthGuardNoRedirect {
  private authService = inject(AuthService);
  private settingsService = inject(SettingsService);

  /**
   *  Checks if the route can be activated based on valid base URL and authentication token.
   * @returns {boolean} `true` if the route can be activated; otherwise, `false`.
   */
  canActivate() {
    return this.settingsService.hasValidBaseUrl() && this.authService.hasValidToken();
  }
}
